import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import { categories, comics } from "@/mocks/comics";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || "all");

  useEffect(() => {
    if (slug) setActiveCat(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeCat]);

  const currentCategory = categories.find((c) => c.slug === activeCat);
  const categoryName = currentCategory?.name || "全部";

  const filteredComics =
    activeCat === "all"
      ? comics
      : comics.filter((c) => c.category === activeCat);

  return (
    <main>
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://073manga.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: `${categoryName}漫画`,
                item: `https://073manga.com/category/${activeCat}`,
              },
            ],
          }),
        }}
      />

      <Navbar />

      {/* Page Header */}
      <section className="pt-24 pb-8 bg-background-50">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">{categoryName}漫画</span>
          </nav>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            {categoryName}漫画
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            {activeCat === "all"
              ? "浏览全部韩国漫画资源，发现您喜欢的作品"
              : `精选${categoryName}类韩国漫画，共 ${filteredComics.length} 部作品`}
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCat(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCat === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comic Grid */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  cover={comic.cover}
                  author={comic.author}
                  status={comic.status}
                  rating={comic.rating}
                  views={comic.views}
                  category={comic.category}
                  tags={comic.tags}
                  updateDate={comic.updateDate}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                <i className="ri-book-2-line text-4xl"></i>
              </span>
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
            </div>
          )}

          {/* Pagination */}
          {filteredComics.length > 8 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium">
                1
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors">
                2
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}